import { Action, Module, Mutation } from "vuex-class-modules"

import {
  DemoVideo,
  DemoVideoCreatePayload,
  DemoVideoDeletePayload,
  IDemoVideo,
  DemoVideoListPayload
} from "@/models"
import { BaseModule } from "@/models/BaseModule"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"

import { Modules } from "../modules"
import { store } from "../store"
import { accountModule } from "./account.module"
import { projectModule } from "./project.module"

export enum DemoVideoActions {
  GET_DEMO_VIDEOS = "GET_DEMO_VIDEOS",
  CREATE_DEMO_VIDEO = "CREATE_DEMO_VIDEO",
  DELETE_DEMO_VIDEO = "DELETE_DEMO_VIDEO",
  RESET_DEMO_VIDEOS = "RESET_DEMO_VIDEOS"
}

@Module
class DemoVideoModule extends BaseModule {
  demoVideos: DemoVideo[] = []
  demoVideosRequestId = 0

  get currentDemoVideo() {
    return this.demoVideos.find(demoVideo => demoVideo.current) ?? null
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get accountId() {
    return accountModule.accountId
  }

  @Mutation
  setDemoVideos(demoVideos: DemoVideo[]) {
    this.demoVideos = demoVideos
  }

  @Mutation
  setDemoVideosRequestId(requestId: number) {
    this.demoVideosRequestId = requestId
  }

  /**
   * Loads the demo video versions for a resource and stores the normalized list.
   * The backend returns the current demo video as part of the same version list.
   * Same requestId-guard pattern used elsewhere in the project (moveTo.ts,
   * ModalInviteToTeam.vue): a slower, earlier-issued call can't overwrite the
   * store with stale data after a faster, later one has already resolved.
   */
  @Action
  async [DemoVideoActions.GET_DEMO_VIDEOS](payload: DemoVideoListPayload) {
    const projectId = payload.projectId ?? this.projectId
    const requestId = this.demoVideosRequestId + 1
    this.setDemoVideosRequestId(requestId)

    const data = await apiService.get<IDemoVideo[]>(
      Endpoint.GET_DEMO_VIDEOS(
        this.accountId,
        projectId,
        payload.resourceType,
        payload.targetId
      )
    )

    return this.handleResponse<IDemoVideo[]>(data, demoVideos => {
      const videos = demoVideos.map(demoVideo => DemoVideo.create(demoVideo))

      if (requestId === this.demoVideosRequestId) {
        this.setDemoVideos(videos)
      }
      return videos
    }, true)
  }

  @Action
  async [DemoVideoActions.CREATE_DEMO_VIDEO](payload: DemoVideoCreatePayload) {
    const projectId = payload.projectId ?? this.projectId
    const data = await apiService.post<IDemoVideo>(
      Endpoint.CREATE_DEMO_VIDEO(
        this.accountId,
        projectId,
        payload.resourceType,
        payload.targetId
      ),
      payload.fileMetadataId
        ? { fileMetadataId: payload.fileMetadataId }
        : { videoUrl: payload.videoUrl }
    )

    return this.handleResponse<IDemoVideo>(data, demoVideo => DemoVideo.create(demoVideo), true)
  }

  @Action
  async [DemoVideoActions.DELETE_DEMO_VIDEO](payload: DemoVideoDeletePayload) {
    const projectId = payload.projectId ?? this.projectId
    const data = await apiService.delete<void>(
      Endpoint.DELETE_DEMO_VIDEO(this.accountId, projectId, payload.demoVideoId)
    )

    return this.handleResponse<void>(data, () => true, true)
  }

  @Action
  [DemoVideoActions.RESET_DEMO_VIDEOS]() {
    this.setDemoVideos([])
  }
}

export const demoVideoModule = new DemoVideoModule({
  store,
  name: Modules.DEMO_VIDEO
})

export async function fetchDemoVideoVersions(payload: DemoVideoListPayload): Promise<DemoVideo[]> {
  const result = await demoVideoModule[DemoVideoActions.GET_DEMO_VIDEOS](payload)
  return Array.isArray(result) ? result : []
}
